import React, { useEffect, useState } from "react";
import { Row, Col, Space, Button, Tabs, Dropdown, Modal, message } from "antd";
import PatientGalleryUpload from "./PatientGalleryUpload";
import {
  getPatientUploadsApi,
  updateIpGalleryFoldersApi,
} from "../../../services/patient.services";
import useQuery from "../../../hooks/useQuery";
import PatientImageView from "./PatientImageView";
import { UploadOutlined } from "@ant-design/icons";
import AddLabelModal from "./AddLabelModal";
import IpUploadModal from "../../Receptionist/Inpatients/IpUploadModal";
import {
  folderHasUploads,
  GALLERY_FOLDER_KEYS,
} from "../../../helpers/galleryUtils";
import GalleryLabelTab from "./GalleryLabelTab";
import "./GalleryLabelTab.css";

const PatientGallery = ({ patientData }) => {
  const [fileList, setFileList] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lableModal, setLableModal] = useState(false);
  const [refreshLabels, setRefreshLabels] = useState(false);
  const [galleryModal, setGalleryModal] = useState(false);
  const [activeKey, seActiveKey] = useState(0);
  const isSpecialTab =
    activeKey === GALLERY_FOLDER_KEYS.PATIENT_FILES ||
    activeKey === GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY ||
    activeKey === GALLERY_FOLDER_KEYS.CONSENT_FORM ||
    activeKey === GALLERY_FOLDER_KEYS.RECYCLE_BIN;
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState("consent");
  const params = useQuery();
  const isActive =
    params?.status === "current" || patientData?.admission?.is_active;
  const [localFolders, setLocalFolders] = useState([]);
  const galleryFolders = localFolders;
  const refreshPage = () => setRefresh((prev) => !prev);
  const refreshLabelsPage = () => refreshPage();
  const getFolderNameByKey = (key) => {
    const category = categories.find((item) => item.id === key);
    return category?.name || "";
  };

  const openUploadFor = (uploadType) => {
    if (uploadType === "gallery") {
      setGalleryModal(true);
      return;
    }
    if (uploadType === "patient_files") {
      setType("patient_files");
      setOpenModal(true);
      return;
    }
    if (uploadType === "discharge") {
      setType("discharge");
      setOpenModal(true);
      return;
    }
    if (uploadType === "consent") {
      setType("consent");
      setOpenModal(true);
    }
  };

  const handleTabChange = (value) => {
    seActiveKey(value);
    setLoading(true);
  };

  const handleTabEdit = (targetKey, action) => {
    if (action === "add") {
      setLableModal(true);
    }
  };

  const confirmDeleteEmptyLabel = (folderName, targetKey) => {
    Modal.confirm({
      title: `Delete label "${folderName}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          const res = await getPatientUploadsApi(
            `ip_admission_id=${Number(
              params?.patient
            )}&folder=${encodeURIComponent(folderName)}`
          );

          if (folderHasUploads(res?.data)) {
            message.error(
              "Cannot delete label with files. Remove or move files first."
            );
            return;
          }

          const updatedFolders = galleryFolders.filter((f) => f !== folderName);
          await updateIpGalleryFoldersApi(patientData?.admission?.id, {
            gallery_folders: updatedFolders,
          });

          setLocalFolders(updatedFolders);
          if (activeKey == targetKey) {
            seActiveKey(0);
          }
          message.success("Label deleted successfully");
          refreshPage();
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to delete label"
          );
        }
      },
    });
  };

  useEffect(() => {
    setLoading(true);
    const admissionId = Number(params?.patient);
    let queryFilters = `ip_admission_id=${admissionId}`;

    if (activeKey === GALLERY_FOLDER_KEYS.PATIENT_FILES) {
      queryFilters += `&category_type=${GALLERY_FOLDER_KEYS.PATIENT_FILES}`;
    } else if (activeKey === GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY) {
      queryFilters += `&category_type=${GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY}`;
    } else if (activeKey === GALLERY_FOLDER_KEYS.CONSENT_FORM) {
      queryFilters += `&category_type=${GALLERY_FOLDER_KEYS.CONSENT_FORM}`;
    } else if (activeKey === GALLERY_FOLDER_KEYS.RECYCLE_BIN) {
      queryFilters += `&category_type=${GALLERY_FOLDER_KEYS.RECYCLE_BIN}`;
    } else if (activeKey !== 0) {
      queryFilters += `&folder=${encodeURIComponent(
        getFolderNameByKey(activeKey)
      )}`;
    }

    getPatientUploadsApi(queryFilters)
      .then((res) => setFileList(res?.data ?? []))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [refresh, activeKey]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const mapped = galleryFolders.map((name, index) => ({
      id: index + 1,
      name,
    }));

    setCategories(mapped);
  }, [galleryFolders]);

  useEffect(() => {
    if (patientData?.admission?.ip_id) {
      const folders = patientData?.admission?.gallery_folders || [];

      setLocalFolders(folders);
    }
  }, [patientData]);

  return (
    <Row gutter={[12, 12]} className="patient-gallery">
      <Col span={24} className="gallery-actions-col">
        {isActive && (
          <div className="gallery-actions-row">
            <Space size={[8, 8]} wrap={false}>
              {activeKey !== GALLERY_FOLDER_KEYS.RECYCLE_BIN && (
                <Button
                  type="primary"
                  ghost
                  onClick={() => setLableModal(true)}
                >
                  Add Label
                </Button>
              )}

              {activeKey !== GALLERY_FOLDER_KEYS.RECYCLE_BIN && (
                <>
                  {activeKey === 0 ? (
                    <Dropdown
                      trigger={["click"]}
                      placement="bottomLeft"
                      menu={{
                        onClick: ({ key }) => openUploadFor(key),
                        items: [
                          { key: "gallery", label: "Upload" },
                          {
                            key: "patient_files",
                            label: "Upload Patient Files",
                          },
                          {
                            key: "discharge",
                            label: "Upload Discharge Summary",
                          },
                          { key: "consent", label: "Upload Consent Form" },
                        ],
                      }}
                    >
                      <Button className="inv-btn" type="primary">
                        <UploadOutlined />
                        Upload
                      </Button>
                    </Dropdown>
                  ) : activeKey === GALLERY_FOLDER_KEYS.PATIENT_FILES ? (
                    <Button
                      className="inv-btn"
                      type="primary"
                      onClick={() => openUploadFor("patient_files")}
                    >
                      <UploadOutlined />
                      Upload Patient Files
                    </Button>
                  ) : activeKey === GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY ? (
                    <Button
                      className="inv-btn"
                      type="primary"
                      onClick={() => openUploadFor("discharge")}
                    >
                      <UploadOutlined />
                      Upload Discharge Summary
                    </Button>
                  ) : activeKey === GALLERY_FOLDER_KEYS.CONSENT_FORM ? (
                    <Button
                      className="inv-btn"
                      type="primary"
                      onClick={() => openUploadFor("consent")}
                    >
                      <UploadOutlined />
                      Upload Consent Form
                    </Button>
                  ) : (
                    <Button
                      className="inv-btn"
                      type="primary"
                      onClick={() => openUploadFor("gallery")}
                    >
                      <UploadOutlined />
                      Upload
                    </Button>
                  )}
                </>
              )}
            </Space>
          </div>
        )}
      </Col>
      <Row
        gutter={0}
        style={{
          width: "100%",
          background: "#fff",
        }}
      >
        <Col span={24}>
          <Tabs
            type={isActive ? "editable-card" : "card"}
            className="patient-gallery-tabs"
            onChange={handleTabChange}
            onEdit={isActive ? handleTabEdit : undefined}
            items={[
              { label: "All", key: 0, closable: false },
              ...categories?.map((item) => ({
                label: (
                  <GalleryLabelTab
                    name={item?.name}
                    isSelected={activeKey === item?.id}
                    isEmpty={
                      !loading && !folderHasUploads(fileList)
                    }
                    canDelete={isActive}
                    onDelete={() =>
                      confirmDeleteEmptyLabel(item?.name, item?.id)
                    }
                  />
                ),
                key: item?.id,
                closable: false,
              })),
              {
                label: "Discharge Summary",
                key: GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
                closable: false,
              },
              {
                label: "Consent Form",
                key: GALLERY_FOLDER_KEYS.CONSENT_FORM,
                closable: false,
              },
              {
                label: "Patient Files",
                key: GALLERY_FOLDER_KEYS.PATIENT_FILES,
                closable: false,
              },
              {
                label: "Recycle Bin",
                key: GALLERY_FOLDER_KEYS.RECYCLE_BIN,
                closable: false,
              },
            ]}
            style={{
              background: "white",
              display: "block",
              padding: "5px",
              width: "100%",
            }}
          />
        </Col>

        <Col span={24} style={{ width: "100%", background: "#fff" }}>
          <PatientImageView
            data={fileList}
            isLoading={loading}
            refresh={refreshPage}
            patientData={patientData}
            activeKey={activeKey}
            setLoading={setLoading}
          />
        </Col>
      </Row>
      {lableModal && (
        <AddLabelModal
          refresh={refreshPage}
          isModalVisible={lableModal}
          setIsModalVisible={setLableModal}
          labels={categories}
          refreshLabelsPage={refreshLabelsPage}
          ipId={patientData?.admission?.id}
          existingFolders={galleryFolders}
          onLocalAdd={(label) =>
            setLocalFolders((prev) => [...new Set([...prev, label])])
          }
        />
      )}
      {galleryModal && (
        <PatientGalleryUpload
          refresh={refreshPage}
          isModalVisible={galleryModal}
          setIsModalVisible={setGalleryModal}
          labels={categories}
          refreshLabelsPage={refreshLabelsPage}
          ipId={patientData?.admission?.ip_id}
          folder={activeKey === 0 ? "" : getFolderNameByKey(activeKey)}
        />
      )}
      {openModal && (
        <IpUploadModal
          setOpenModal={setOpenModal}
          openModal={openModal}
          editData={{ id: params?.patient }}
          type={type}
          refresh={refreshPage}
        />
      )}
    </Row>
  );
};
export default PatientGallery;
